import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Skeleton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';

import { fetcher, publicApiAuditLogApi } from '../../lib/api';
import styles from './PublicApiAuditLogs.module.scss';

const PAGE_SIZE = 40;

const OUTCOME_STYLES = {
    added: { background: 'var(--spara-badge-green-bg)', color: 'var(--spara-badge-green-fg)' },
    removed: { background: 'var(--spara-badge-orange-bg)', color: 'var(--spara-badge-orange-fg)' },
    already_has_access: {
        background: 'var(--spara-badge-info-bg)',
        color: 'var(--spara-badge-info-fg)',
    },
    unauthorized: { background: 'var(--spara-badge-fail-bg)', color: 'var(--spara-badge-fail-fg)' },
    invalid_input: { background: 'var(--spara-badge-red-bg)', color: 'var(--spara-badge-red-fg)' },
    not_found: {
        background: 'var(--spara-badge-purple-bg)',
        color: 'var(--spara-badge-purple-fg)',
    },
    group_inactive: {
        background: 'var(--spara-badge-orange-bg)',
        color: 'var(--spara-badge-orange-fg)',
    },
    error: { background: 'var(--spara-badge-fail-bg)', color: 'var(--spara-badge-fail-fg)' },
    success: { background: 'var(--spara-badge-green-bg)', color: 'var(--spara-badge-green-fg)' },
    client_error: {
        background: 'var(--spara-badge-pending-bg)',
        color: 'var(--spara-badge-pending-fg)',
    },
    server_error: { background: 'var(--spara-badge-fail-bg)', color: 'var(--spara-badge-fail-fg)' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const PublicApiAuditLogs = () => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [outcome, setOutcome] = useState('all');
    const [rows, setRows] = useState([]);
    const [expandedId, setExpandedId] = useState(null);

    const listUrl = publicApiAuditLogApi.list({
        page,
        limit: PAGE_SIZE,
        ...(outcome !== 'all' ? { outcome } : {}),
    });

    const { data, error, isLoading } = useSWR(listUrl, fetcher, {
        refreshInterval: 15000,
    });

    useEffect(() => {
        if (!data?.results) return;
        setRows((prev) => {
            if (page === 1) return data.results;
            const byId = new Map(prev.map((r) => [r.id, r]));
            data.results.forEach((r) => byId.set(r.id, r));
            return Array.from(byId.values());
        });
    }, [data, page]);

    const filters = useMemo(
        () => [
            { id: 'all', label: t('public_api_audit_logs.filter_all') },
            { id: 'added', label: t('public_api_audit_logs.outcome_added') },
            { id: 'removed', label: t('public_api_audit_logs.outcome_removed') },
            { id: 'already_has_access', label: t('public_api_audit_logs.outcome_already_has_access') },
            { id: 'unauthorized', label: t('public_api_audit_logs.outcome_unauthorized') },
            { id: 'invalid_input', label: t('public_api_audit_logs.outcome_invalid_input') },
            { id: 'not_found', label: t('public_api_audit_logs.outcome_not_found') },
            { id: 'error', label: t('public_api_audit_logs.outcome_error') },
        ],
        [t]
    );

    const outcomeLabel = (id) => {
        const map = {
            added: t('public_api_audit_logs.outcome_added'),
            removed: t('public_api_audit_logs.outcome_removed'),
            already_has_access: t('public_api_audit_logs.outcome_already_has_access'),
            unauthorized: t('public_api_audit_logs.outcome_unauthorized'),
            invalid_input: t('public_api_audit_logs.outcome_invalid_input'),
            not_found: t('public_api_audit_logs.outcome_not_found'),
            group_inactive: t('public_api_audit_logs.outcome_group_inactive'),
            error: t('public_api_audit_logs.outcome_error'),
            success: t('public_api_audit_logs.outcome_success'),
            client_error: t('public_api_audit_logs.outcome_client_error'),
            server_error: t('public_api_audit_logs.outcome_server_error'),
        };
        return map[id] || id;
    };

    const totalCount = data?.count ?? rows.length;
    const hasMore = rows.length > 0 && rows.length < totalCount;

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle title={t('menu.public_api_audit_logs')} />
            </GridItem>
            <GridItem xs={12}>
                <Card>
                    <CardBody>
                        <div className={styles.listHeader}>
                            <div className={styles.listHeaderCount}>
                                {totalCount} {t('menu.public_api_audit_logs').toLowerCase()}
                            </div>
                            <div className={styles.filters}>
                                {filters.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        className={`${styles.filterChip} ${
                                            outcome === f.id ? styles.filterChipActive : ''
                                        }`}
                                        onClick={() => {
                                            setOutcome(f.id);
                                            setPage(1);
                                            setRows([]);
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className={styles.empty}>
                                {error?.response?.data?.message || t('common.error')}
                            </div>
                        )}

                        <div className={styles.tableWrap}>
                            <Table>
                                <TableHead className={styles.tableHead}>
                                    <TableRow>
                                        <TableCell>{t('public_api_audit_logs.col_outcome')}</TableCell>
                                        <TableCell>{t('public_api_audit_logs.col_request')}</TableCell>
                                        <TableCell>{t('public_api_audit_logs.col_customer')}</TableCell>
                                        <TableCell>{t('public_api_audit_logs.col_member')}</TableCell>
                                        <TableCell>{t('public_api_audit_logs.col_status')}</TableCell>
                                        <TableCell>{t('public_api_audit_logs.col_created')}</TableCell>
                                        <TableCell>{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={styles.tableBody}>
                                    {isLoading && page === 1 && rows.length === 0 &&
                                        Array.from({ length: 6 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 7 }).map((__, j) => (
                                                    <TableCell key={j}>
                                                        <Skeleton variant="text" width={80} />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    {!isLoading && rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <div className={styles.empty}>
                                                    {t('public_api_audit_logs.empty')}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows.map((row) => (
                                        <React.Fragment key={row.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <span
                                                        className={styles.badge}
                                                        style={
                                                            OUTCOME_STYLES[row.outcome] ||
                                                            OUTCOME_STYLES.success
                                                        }
                                                    >
                                                        {outcomeLabel(row.outcome)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        {row.http_method} {row.path}
                                                    </div>
                                                    <div className={styles.progressLabel}>
                                                        {row.latency_ms != null
                                                            ? `${row.latency_ms} ms`
                                                            : '—'}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div>{row.customer || '—'}</div>
                                                    <div className={styles.progressLabel}>
                                                        {row.special_company_id
                                                            ? `group #${row.special_company_id}`
                                                            : '—'}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className={styles.mono}>
                                                        {row.member_identifier_mask || '—'}
                                                    </div>
                                                </TableCell>
                                                <TableCell>{row.http_status}</TableCell>
                                                <TableCell>{formatDate(row.created_at)}</TableCell>
                                                <TableCell>
                                                    <button
                                                        type="button"
                                                        className={styles.actionBtnPrimary}
                                                        onClick={() =>
                                                            setExpandedId(
                                                                expandedId === row.id
                                                                    ? null
                                                                    : row.id
                                                            )
                                                        }
                                                    >
                                                        {expandedId === row.id
                                                            ? t('public_api_audit_logs.hide_details')
                                                            : t('public_api_audit_logs.show_details')}
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                            {expandedId === row.id && (
                                                <TableRow>
                                                    <TableCell colSpan={7}>
                                                        <div className={styles.metaGrid}>
                                                            <div>
                                                                <div className={styles.metaLabel}>
                                                                    {t('public_api_audit_logs.detail_token_id')}
                                                                </div>
                                                                <div className={styles.metaValue}>
                                                                    {row.access_token_id ?? '—'}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className={styles.metaLabel}>
                                                                    {t('public_api_audit_logs.detail_ip')}
                                                                </div>
                                                                <div className={styles.metaValue}>
                                                                    {row.ip_address || '—'}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className={styles.metaLabel}>
                                                                    {t('public_api_audit_logs.detail_hash')}
                                                                </div>
                                                                <div className={`${styles.metaValue} ${styles.mono}`}>
                                                                    {row.member_identifier_hash || '—'}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className={styles.metaLabel}>
                                                                    {t('public_api_audit_logs.detail_ua')}
                                                                </div>
                                                                <div className={styles.metaValue}>
                                                                    {row.user_agent || '—'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {row.error_message && (
                                                            <div className={styles.exceptionBox}>
                                                                {row.error_message}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {hasMore && (
                            <div className={styles.loadMore}>
                                <SparaButton color="primary" onClick={() => setPage((p) => p + 1)}>
                                    {t('public_api_audit_logs.load_more')}
                                </SparaButton>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default PublicApiAuditLogs;
